/**
 * VAT Calculation Utilities for Belgium (21% TVA)
 * 
 * Rules:
 * - TTC (price including VAT) is the source of truth
 * - HTVA is calculated by flooring to ensure TTC - HTVA = TVA exactly
 * - This prevents rounding errors like 80.00 TTC becoming 80.01 after recalculation
 */

const VAT_RATE = 0.21; // 21% Belgium VAT

/**
 * Calculate HTVA (price excluding VAT) from TTC price
 * Uses floor rounding to 2 decimals to ensure exact match when recalculating
 * @param {number} priceTTC - Price including VAT
 * @returns {number} - Price excluding VAT (HTVA)
 */
export const calculateHTVA = (priceTTC) => {
  // HTVA = TTC / 1.21
  // Floor to 2 decimals to ensure TTC calculation is exact or slightly higher
  const htva = priceTTC / (1 + VAT_RATE);
  return Math.floor(htva * 100) / 100;
};

/**
 * Calculate TVA amount from TTC price
 * TVA = TTC - HTVA (ensures exact match)
 * @param {number} priceTTC - Price including VAT
 * @returns {number} - VAT amount
 */
export const calculateTVA = (priceTTC) => {
  const htva = calculateHTVA(priceTTC);
  // Round to 2 decimals to avoid floating point issues
  return Math.round((priceTTC - htva) * 100) / 100;
};

/**
 * Calculate TTC from HTVA price
 * @param {number} priceHTVA - Price excluding VAT
 * @returns {number} - Price including VAT
 */
export const calculateTTC = (priceHTVA) => {
  return Math.round(priceHTVA * (1 + VAT_RATE) * 100) / 100;
};

/**
 * Get all VAT components from TTC price
 * @param {number} priceTTC - Price including VAT
 * @returns {Object} - { htva, tva, ttc }
 */
export const getVATBreakdown = (priceTTC) => {
  const htva = calculateHTVA(priceTTC);
  const tva = Math.round((priceTTC - htva) * 100) / 100;
  return {
    htva,
    tva,
    ttc: priceTTC,
  };
};

/**
 * Get all VAT components from HTVA price
 * @param {number} priceHTVA - Price excluding VAT
 * @returns {Object} - { htva, tva, ttc }
 */
export const getVATBreakdownFromHTVA = (priceHTVA) => {
  const ttc = calculateTTC(priceHTVA);
  const tva = Math.round((ttc - priceHTVA) * 100) / 100;
  return {
    htva: priceHTVA,
    tva,
    ttc,
  };
};

/**
 * Format price with € symbol
 * @param {number} price - Price to format
 * @returns {string} - Formatted price
 */
export const formatPrice = (price) => {
  return `€${price.toFixed(2)}`;
};

/**
 * Format price breakdown display
 * @param {number} priceTTC - Price including VAT
 * @param {string} language - 'fr' or 'nl'
 * @returns {Object} - { display, htva, tva, ttc }
 */
export const formatPriceBreakdown = (priceTTC, language = 'fr') => {
  const { htva, tva, ttc } = getVATBreakdown(priceTTC);
  const labels = language === 'fr' 
    ? { htva: 'HTVA', tva: 'TVA 21%', ttc: 'TTC' }
    : { htva: 'excl. BTW', tva: 'BTW 21%', ttc: 'incl. BTW' };
  
  return {
    htva,
    tva,
    ttc,
    display: {
      htva: `€${htva.toFixed(2)} ${labels.htva}`,
      tva: `€${tva.toFixed(2)} (${labels.tva})`,
      ttc: `€${ttc.toFixed(2)} ${labels.ttc}`,
    },
  };
};

export default {
  VAT_RATE,
  calculateHTVA,
  calculateTVA,
  calculateTTC,
  getVATBreakdown,
  getVATBreakdownFromHTVA,
  formatPrice,
  formatPriceBreakdown,
};
